import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { SEARCH_SHORTCUT } from "../lib/platform";
import { Search as SearchIcon, X, Hammer, Users, FileSpreadsheet, FileText, BookOpen, ClipboardList } from "lucide-react";
import { VISIBILITY_META } from "../lib/taskVisibility";

export default function GlobalSearch({ open, onClose }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 50);
        else { setQ(""); setResults(null); }
    }, [open]);

    useEffect(() => {
        if (!q.trim() || !open) { setResults(null); return; }
        setLoading(true);
        const t = setTimeout(() => {
            api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).finally(() => setLoading(false));
        }, 200);
        return () => clearTimeout(t);
    }, [q, open]);

    const go = (path) => { onClose(); navigate(path); };

    if (!open) return null;

    const sections = results ? [
        { key: "jobs", label: "Zakázky", icon: Hammer, items: results.jobs, render: (j) => ({ title: j.title, sub: j.client_name || "—", path: `/zakazky/${j.id}` }) },
        { key: "tasks", label: "Úkoly", icon: ClipboardList, items: results.tasks || [], render: (t) => ({ title: t.title, sub: [VISIBILITY_META[t.visibility]?.label, t.assignee_name && `→ ${t.assignee_name}`, t.due_date && `do ${new Date(t.due_date + "T12:00:00").toLocaleDateString("cs-CZ")}`].filter(Boolean).join(" · "), path: `/ukoly` }) },
        { key: "clients", label: "Klienti", icon: Users, items: results.clients || [], render: (c) => ({ title: c.name, sub: [c.city, c.phone].filter(Boolean).join(" · "), path: `/klienti` }) },
        { key: "budgets", label: "Rozpočty", icon: FileSpreadsheet, items: results.budgets || [], render: (b) => ({ title: b.title, sub: `${b.number} · ${b.client_name || "—"}`, path: `/rozpocty/${b.id}` }) },
        { key: "invoices", label: "Faktury", icon: FileText, items: results.invoices || [], render: (i) => ({ title: i.client_name || i.title, sub: i.number, path: `/faktury/${i.id}` }) },
        { key: "catalog", label: "Ceník", icon: BookOpen, items: results.catalog || [], render: (c) => ({ title: c.name, sub: `${c.category || "—"} · ${c.unit}`, path: `/cenik` }) },
    ].filter(s => s.items?.length > 0) : [];

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-start justify-center pt-[10vh] p-4" onClick={onClose} data-testid="global-search">
            <div className="bg-white border-2 border-slate-900 w-full max-w-2xl max-h-[70vh] flex flex-col sharp-shadow" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 px-5 py-4 border-b-2 border-slate-900">
                    <SearchIcon className="w-5 h-5 text-slate-500" />
                    <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat zakázky, klienty, faktury…"
                           data-testid="search-input"
                           className="flex-1 text-base focus:outline-none bg-transparent" />
                    <kbd className="text-[10px] uppercase tracking-wider text-slate-400 border border-slate-300 px-1.5 py-0.5">ESC</kbd>
                    <button onClick={onClose}><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>
                </div>
                <div className="overflow-y-auto flex-1">
                    {!q.trim() && (
                        <div className="p-10 text-center text-slate-400 text-sm">
                            Začněte psát — najdete cokoliv ve firmě.
                            <div className="mt-3 text-xs">Zkratka: <kbd className="border border-slate-300 px-1.5 py-0.5">{SEARCH_SHORTCUT}</kbd></div>
                        </div>
                    )}
                    {loading && <div className="p-6 text-center text-slate-400 text-xs uppercase tracking-widest">Hledám…</div>}
                    {results && sections.length === 0 && !loading && (
                        <div className="p-10 text-center text-slate-400 text-sm">Nic nenalezeno.</div>
                    )}
                    {sections.map((s) => (
                        <div key={s.key}>
                            <div className="flex items-center gap-2 px-5 py-2 bg-slate-50 border-b border-slate-200">
                                <s.icon className="w-3.5 h-3.5 text-orange-600" />
                                <span className="label-eyebrow">{s.label}</span>
                                <span className="text-xs text-slate-400 ml-auto">{s.items.length}</span>
                            </div>
                            {s.items.map((it, idx) => {
                                const r = s.render(it);
                                return (
                                    <button key={it.id || idx} onClick={() => go(r.path)} data-testid={`search-result-${s.key}-${idx}`}
                                            className="w-full px-5 py-3 text-left hover:bg-orange-50/50 border-b border-slate-100 flex items-center justify-between">
                                        <div>
                                            <div className="font-medium text-slate-900">{r.title}</div>
                                            <div className="text-xs text-slate-500">{r.sub}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
